// iOS-style page slide on navigation, where the browser can do it for free.
//
// Uses the View Transitions API (Chrome 111+, Safari 18+). Everywhere else
// (Chrome 86 tablets, iOS 16.4-17) nothing here runs and the 150ms fade in
// motion.ts stays exactly as it was.
//
// WHY SNAPSHOTS, NOT A TRANSFORM: motion.ts forbids a transform on a page.
// <Modal> and <CaptureFab> (and ~100 files' `fixed` elements) render inside the
// page un-portaled, and a transformed ancestor becomes their containing block.
// A view transition animates a SNAPSHOT of the old and new page in a top-layer
// pseudo-element tree, so the real page never gets a transform and nothing
// `fixed` inside it moves. The slide is CSS at the end of index.css; this file
// only decides WHEN to start one.
//
// BrowserRouter has no viewTransition hook (that is data-router only), so
// main.tsx uses <unstable_HistoryRouter> with a history whose listen() wraps
// each router update in document.startViewTransition(). The callback uses
// flushSync so the new page is in the DOM when the browser takes the "new"
// snapshot. Do NOT enable the v7_startTransition router flag: it wraps the
// router's setState in React.startTransition, which flushSync cannot force.
//
// SKIPPED (the update runs straight through, no animation):
//  - REPLACE: a redirect or a URL tidy-up is not a "move to another page".
//  - Same pathname: a filter / open record in ?search or #hash is the same page.
//  - POP right after a popstate the browser already animated itself
//    (`hasUAVisualTransition`, iOS Safari's swipe-back): animating again
//    would play two slides.
//  - Reduced motion, hidden tab, no startViewTransition.
//
// NOT COVERED: lazy route chunks. flushSync commits the Suspense fallback, so a
// cold chunk slides in the loader and the page replaces it afterwards. That is
// acceptable: a warm chunk (the normal case after first visit) slides the page.
import { createBrowserHistory, Action, type History } from '@remix-run/router'
import { flushSync } from 'react-dom'
import { prefersReducedMotion } from '@/lib/motion'

const ATTR = 'pageNav'
let seq = 0

// Recorded at module init, i.e. BEFORE the router's own popstate listener, so
// it is already set when the router's update arrives.
let lastPopWasUaAnimated = false
if (typeof window !== 'undefined') {
  window.addEventListener('popstate', e => {
    lastPopWasUaAnimated = (e as PopStateEvent & { hasUAVisualTransition?: boolean }).hasUAVisualTransition === true
  })
}

/** True while a page slide is running (the attribute is set on <html>). The
 *  RailShell fade checks it: the slide replaces the fade, never both. */
export function pageTransitionActive(): boolean {
  return typeof document !== 'undefined' && document.documentElement.dataset[ATTR] !== undefined
}

type Listener = Parameters<History['listen']>[0]
type Update = Parameters<Listener>[0]

/** Environment checks: can this browser, right now, run a page slide at all? */
function canSlide(): boolean {
  if (typeof document === 'undefined' || typeof document.startViewTransition !== 'function') return false
  if (prefersReducedMotion()) return false
  return document.visibilityState !== 'hidden'
}

/** Navigation checks: is this update a move to another page? */
function isPageMove(update: Update, prevPath: string): boolean {
  if (update.action !== Action.Push && update.action !== Action.Pop) return false
  if (update.location.pathname === prevPath) return false
  return !(update.action === Action.Pop && lastPopWasUaAnimated)
}

function shouldAnimate(update: Update, prevPath: string): boolean {
  return canSlide() && isPageMove(update, prevPath)
}

function run(update: Update, fn: Listener, prevPath: string): void {
  if (!shouldAnimate(update, prevPath)) { fn(update); return }
  const root = document.documentElement
  root.dataset[ATTR] = update.action === Action.Push ? 'forward' : 'back'
  // A second tap mid-slide starts a new transition and skips this one, whose
  // `finished` then settles early: only the LATEST slide may clear the attribute.
  const mine = ++seq
  try {
    const t = document.startViewTransition(() => { flushSync(() => fn(update)) })
    const clear = () => { if (seq === mine) delete root.dataset[ATTR] }
    t.finished.then(clear, clear)
    // `ready` REJECTS whenever the animation does not run: a second tap skips
    // this slide ("Transition was skipped"), or the tab hides mid-slide
    // ("Transition was aborted because of invalid state"). The page update
    // itself still happens. Unobserved, that rejection reached the error
    // reporter as an unhandled rejection (support digest, 4 Nov). Only the
    // animation is lost, so there is nothing to report.
    t.ready?.catch(() => {})
  } catch {
    delete root.dataset[ATTR]
    fn(update)
  }
}

export function createTransitionHistory(): History {
  const base = createBrowserHistory({ v5Compat: true })
  // The path the router last rendered; the update itself carries only the new one.
  let current = base.location.pathname
  const wrapped: History = new Proxy(base, {
    get(target, prop) {
      if (prop === 'listen') {
        return (fn: Listener) => target.listen(update => {
          const prev = current
          current = update.location.pathname
          run(update, fn, prev)
        })
      }
      return Reflect.get(target, prop, target)
    },
  })
  return wrapped
}
